import { ClaimDto } from "@growth/dto";
import { ClaimKind, ClaimStatus } from "@growth/schema";
import { Badge, Button, ConfirmDialog, Field, Input, Textarea } from "@growth/ui";
import { LoaderCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ErrorText, PageHeader, selectClass } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useClaims, useDeleteClaim, useSaveClaim } from "../lib/workspace";
import { SuggestedClaims } from "./setup";

const STATUS_LABEL: Record<ClaimStatus, string> = {
  ACTIVE: "In use",
  WITHHELD: "Withheld",
  RETIRED: "Retired",
  SUGGESTED: "Suggested",
};

const StatusBadge = ({ status }: { status: ClaimStatus }) => (
  <Badge tone={status === "ACTIVE" ? "success" : "neutral"}>{STATUS_LABEL[status]}</Badge>
);

type Draft = {
  id?: string;
  kind: ClaimKind;
  status: ClaimStatus;
  code: string;
  text: string;
  source: string;
  note: string;
};

const blank = (kind: ClaimKind): Draft => ({
  kind,
  status: "ACTIVE",
  code: "",
  text: "",
  source: "",
  note: "",
});
const fromClaim = (claim: ClaimDto): Draft => ({ ...claim, code: claim.code ?? "" });

/**
 * The only facts content may state, and what it must never say. Agents read this before writing
 * and the claims check blocks anything not on it. Only people change it.
 */
export const ClaimsPage = () => {
  const { brand, atLeast } = useActive();
  const canEdit = atLeast("EDITOR");
  const claims = useClaims();
  const [draft, setDraft] = useState<Draft | null>(null);

  const approved =
    claims.data?.filter((claim) => claim.kind === "APPROVED" && claim.status !== "SUGGESTED") ?? [];
  const suggested = claims.data?.filter((claim) => claim.status === "SUGGESTED") ?? [];
  const banned = claims.data?.filter((claim) => claim.kind === "BANNED") ?? [];

  return (
    <section className="max-w-5xl space-y-8">
      <PageHeader
        eyebrow={brand?.name ?? ""}
        title="Claims"
        actions={
          canEdit &&
          !draft && (
            <Button onClick={() => setDraft(blank("APPROVED"))}>
              <Plus className="size-4" />
              New claim
            </Button>
          )
        }
      >
        Content may only state what's approved and in use here, citing the code. Withheld claims are
        true but not usable yet. Banned lines are never said, in any wording.
      </PageHeader>

      {draft && <ClaimForm draft={draft} onDone={() => setDraft(null)} />}
      {claims.isError && <ErrorText>{apiErrorMessage(claims.error)}</ErrorText>}
      {claims.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}

      {suggested.length > 0 && <SuggestedClaims />}

      {claims.data && (
        <>
          <ClaimTable
            title={`Approved (${approved.filter((claim) => claim.status === "ACTIVE").length} in use)`}
            claims={approved}
            onEdit={canEdit ? (claim) => setDraft(fromClaim(claim)) : undefined}
          />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Banned ({banned.length})</h2>
              {canEdit && !draft && (
                <Button variant="secondary" onClick={() => setDraft(blank("BANNED"))}>
                  <Plus className="size-4" />
                  Ban something
                </Button>
              )}
            </div>
            <ClaimTable
              claims={banned}
              onEdit={canEdit ? (claim) => setDraft(fromClaim(claim)) : undefined}
            />
          </div>
        </>
      )}
    </section>
  );
};

const ClaimTable = ({
  title,
  claims,
  onEdit,
}: {
  title?: string;
  claims: ClaimDto[];
  onEdit?: (claim: ClaimDto) => void;
}) => {
  const remove = useDeleteClaim();
  return (
    <div className="space-y-2">
      {title && <h2 className="text-sm font-semibold">{title}</h2>}
      <div className="overflow-x-auto rounded-xl border border-line bg-white">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-line">
            {claims.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-muted">None yet.</td>
              </tr>
            )}
            {claims.map((claim) => (
              <tr key={claim.id} className={claim.status === "RETIRED" ? "opacity-50" : undefined}>
                {claim.kind === "APPROVED" && (
                  <td className="w-16 px-4 py-3 align-top font-mono text-xs font-semibold">
                    {claim.code}
                  </td>
                )}
                <td className="px-4 py-3 align-top">
                  <p className="text-ink">{claim.text}</p>
                  {claim.source && (
                    <p className="mt-1 text-xs text-muted">Source: {claim.source}</p>
                  )}
                  {claim.note && <p className="mt-1 text-xs text-amber-700">{claim.note}</p>}
                </td>
                <td className="w-24 px-4 py-3 align-top">
                  {claim.kind === "APPROVED" && <StatusBadge status={claim.status} />}
                </td>
                {onEdit && (
                  <td className="w-24 whitespace-nowrap px-2 py-2 text-right align-top">
                    <Button
                      variant="ghost"
                      aria-label={`Edit ${claim.code ?? "banned line"}`}
                      onClick={() => onEdit(claim)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <ConfirmDialog
                      trigger={
                        <Button
                          variant="ghost"
                          aria-label={`Delete ${claim.code ?? "banned line"}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      }
                      title="Delete this claim?"
                      description={
                        claim.kind === "APPROVED"
                          ? "Content that cites it will fail the claims check. To stop using it but keep the record, retire it instead."
                          : "Content may then say this again."
                      }
                      confirmLabel="Delete"
                      destructive
                      onConfirm={() => remove.mutate(claim.id)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ClaimForm = ({ draft, onDone }: { draft: Draft; onDone: () => void }) => {
  const [form, setForm] = useState(draft);
  const save = useSaveClaim();
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setForm({ ...form, [key]: value });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const fields = {
      status: form.status,
      code: form.kind === "APPROVED" ? form.code.trim().toUpperCase() || null : null,
      text: form.text.trim(),
      source: form.source.trim(),
      note: form.note.trim(),
    };
    save.mutate(form.id ? { id: form.id, ...fields } : { kind: form.kind, ...fields }, {
      onSuccess: onDone,
    });
  };

  const approved = form.kind === "APPROVED";
  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-5">
      <h2 className="font-serif text-lg text-ink">
        {form.id ? "Edit" : "New"} {approved ? "claim" : "banned line"}
      </h2>
      {approved && (
        <div className="grid gap-4 sm:grid-cols-[8rem_12rem]">
          <Field label="Code" hint="Agents cite this, e.g. A21">
            {(props) => (
              <Input
                {...props}
                required
                value={form.code}
                maxLength={8}
                onChange={(event) => set("code", event.target.value)}
              />
            )}
          </Field>
          <Field label="Status">
            {(props) => (
              <select
                {...props}
                className={selectClass}
                value={form.status}
                onChange={(event) => set("status", event.target.value as ClaimStatus)}
              >
                <option value="ACTIVE">In use</option>
                <option value="WITHHELD">Withheld (not usable yet)</option>
                <option value="RETIRED">Retired</option>
              </select>
            )}
          </Field>
        </div>
      )}
      <Field label={approved ? "Claim (wording may vary, meaning may not)" : "Never say"}>
        {(props) => (
          <Textarea
            {...props}
            required
            rows={2}
            maxLength={1000}
            value={form.text}
            onChange={(event) => set("text", event.target.value)}
          />
        )}
      </Field>
      {approved && (
        <Field label="Source" hint="Where this is shown to be true: a page, a file, a decision">
          {(props) => (
            <Input
              {...props}
              maxLength={1000}
              value={form.source}
              onChange={(event) => set("source", event.target.value)}
            />
          )}
        </Field>
      )}
      <Field
        label="Note (optional)"
        hint={approved ? "e.g. why it's withheld, or how to say it" : undefined}
      >
        {(props) => (
          <Input
            {...props}
            maxLength={1000}
            value={form.note}
            onChange={(event) => set("note", event.target.value)}
          />
        )}
      </Field>
      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending && <LoaderCircle className="size-4 animate-spin" />}
          Save
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
